import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

const LOGIN_API_URL = `${API_BASE_URL}/login`;
const SIGNUP_API_URL = `${API_BASE_URL}/signup`;
const GET_USER_API_URL = `${API_BASE_URL}/users/me`;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [authMessage, setAuthMessage] = useState(null); 
  
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('luxer_token');
    if (storedToken && storedToken.split('.').length === 3) return storedToken;
    if (storedToken) localStorage.removeItem('luxer_token');
    return null;
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); 

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('luxer_token');
  };

  // --- GLOBAL 401 INTERCEPTOR (FIXED) ---
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if the failure happened on the LOGIN attempt
        const isLoginFailure = error.config && error.config.url === LOGIN_API_URL;

        if (error.response && error.response.status === 401 && !isLoginFailure) {
           // Only run global logout if NOT logging in
           console.warn("401 Detected - Logging out automatically.");
           setAuthMessage("Your session has expired. Please log in again.");
           logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []); 

  const fetchUser = async (currentToken) => {
    if (!currentToken) { setLoading(false); return; }
    try {
      const headers = { 'Authorization': `Bearer ${currentToken}` };
      const response = await axios.get(GET_USER_API_URL, { headers });
      setUser(response.data); 
    } catch (error) {
      console.error("Fetch user failed:", error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          logout();
      } else {
          setAuthMessage("Session lost. Please log in again."); // Network failure message
          logout(); 
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) { setLoading(true); fetchUser(token); } 
    else { setUser(null); setLoading(false); }
  }, [token]); 

  const login = async (username, password) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      const response = await axios.post(LOGIN_API_URL, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (response.data.access_token) {
        loginWithToken(response.data.access_token);
        return true;
      }
    } catch (error) {
      console.error("Login failed:", error);
      // The error is handled locally by returning false
      return false;
    }
  };

  const loginWithToken = (newToken) => {
    localStorage.setItem('luxer_token', newToken); 
    setToken(newToken); 
    setAuthMessage(null);
  };

  const signup = async (email, password, companyName, captchaToken) => {
    try {
      const payload = { email, password, company_name: companyName, captcha_token: captchaToken };
      const response = await axios.post(SIGNUP_API_URL, payload);
      if (response.status === 200) return true; 
      return false;
    } catch (error) {
      console.error("Signup failed", error);
      return false;
    }
  };

  const value = { token, user, setUser, loading, login, loginWithToken, signup, logout, authMessage, setAuthMessage };

  if (loading) return <div className="App"><header className="App-header"><h2>Loading...</h2></header></div>;

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};